//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// This is a registry module, not a component module: it pairs the lazy pane
// bindings with the data and helpers (SETTINGS_GROUPS, getVisibleGroups,
// findLeaf) that reference them, so they can't be split into a component-only
// file. Fast Refresh's "only export components" rule doesn't apply here.
/* eslint-disable react-refresh/only-export-components */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import ScienceIcon from '@mui/icons-material/Science';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DnsIcon from '@mui/icons-material/Dns';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StorageIcon from '@mui/icons-material/Storage';
import ExtensionIcon from '@mui/icons-material/Extension';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldIcon from '@mui/icons-material/Shield';

// Sections are code-split: the modal is mounted at the router root for every
// authenticated route, so eagerly importing every pane (and its MUI/form deps)
// would pull all of them into the initial bundle for users who never open
// Settings. lazy() defers each pane's chunk until it is first shown; the modal
// renders them inside a Suspense boundary.
const ProfileSection = lazy(() => import('./sections/ProfileSection'));
const PreferencesSection = lazy(() => import('./sections/PreferencesSection'));
const ExperimentalSection = lazy(() => import('./sections/ExperimentalSection'));
const AiCredentialsSection =
  lazy(() => import('./sections/AiCredentialsSection'));
const ConnectorsSection = lazy(() => import('./sections/ConnectorsSection'));
const UploadSection = lazy(() => import('./sections/UploadSection'));
const ServerStatusSection = lazy(() => import('./sections/ServerStatusSection'));
const ServerConfigSection =
  lazy(() => import('./sections/ServerConfigSection'));
const PluginsSection = lazy(() => import('./sections/PluginsSection'));
const AiModelsSection = lazy(() => import('./sections/AiModelsSection'));
const AdelSection = lazy(() => import('./sections/AdelSection'));

export interface SettingsLeaf {
  key: string;
  label: string;
  icon: SvgIconComponent;
  component: ComponentType | LazyExoticComponent<ComponentType>;
  // Hidden from admins: used for the read-only Server Config under System,
  // since admins get the editable copy under the Admin group instead.
  hideForAdmin?: boolean;
}

// Which entry point a group is reached from. The split follows scope of
// effect, not topic: `user` changes nothing for anyone else, `server` is
// read-only fact about the graph server, and `admin` changes what every user
// of the deployment sees -- which is why it is reached from its own control
// and never from a personal menu.
export type SettingsScope = 'user' | 'server' | 'admin';

export interface SettingsGroup {
  label: string;
  scope: SettingsScope;
  adminOnly?: boolean;
  leaves: SettingsLeaf[];
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    label: 'General',
    scope: 'user',
    leaves: [
      { key: 'profile', label: 'Profile', icon: PersonIcon,
        component: ProfileSection },
      { key: 'preferences', label: 'Preferences', icon: DisplaySettingsIcon,
        component: PreferencesSection },
      { key: 'experimental', label: 'Experimental', icon: ScienceIcon,
        component: ExperimentalSection }
    ]
  },
  {
    label: 'Connections',
    scope: 'user',
    leaves: [
      { key: 'aiCredentials', label: 'AI Credentials', icon: VpnKeyIcon,
        component: AiCredentialsSection },
      { key: 'connectors', label: 'Connectors', icon: DnsIcon,
        component: ConnectorsSection },
      { key: 'upload', label: 'Upload Defaults', icon: UploadFileIcon,
        component: UploadSection }
    ]
  },
  {
    label: 'System',
    scope: 'server',
    leaves: [
      { key: 'serverStatus', label: 'Server Status', icon: AnalyticsIcon,
        component: ServerStatusSection },
      { key: 'serverConfigView', label: 'Server Config', icon: StorageIcon,
        component: ServerConfigSection, hideForAdmin: true }
    ]
  },
  {
    label: 'Admin',
    scope: 'admin',
    adminOnly: true,
    leaves: [
      { key: 'aiModels', label: 'AI Models', icon: SmartToyIcon,
        component: AiModelsSection },
      { key: 'adel', label: 'AI Data Exposure Layer', icon: ShieldIcon,
        component: AdelSection },
      { key: 'plugins', label: 'Plugins', icon: ExtensionIcon,
        component: PluginsSection },
      { key: 'serverConfigAdmin', label: 'Server Config', icon: StorageIcon,
        component: ServerConfigSection }
    ]
  }
];

export const getVisibleGroups = (
  isAdmin: boolean, scope?: SettingsScope
): SettingsGroup[] =>
  SETTINGS_GROUPS
    .filter(group => scope === undefined || group.scope === scope)
    .filter(group => !group.adminOnly || isAdmin)
    .map(group => ({
      ...group,
      leaves: group.leaves.filter(leaf => !(isAdmin && leaf.hideForAdmin))
    }));

export const DEFAULT_LEAF_KEY = 'profile';

export const findLeaf = (key: string): SettingsLeaf | undefined =>
  SETTINGS_GROUPS.flatMap(g => g.leaves).find(l => l.key === key);
